'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CalculatorResult {
  monthlyPayment: number;
  totalPayment: number;
  totalInterest: number;
  potentialSavings: number;
}

const LoanCalculatorSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [amount, setAmount] = useState<number>(200000);
  const [term, setTerm] = useState<number>(240);
  const [interestRate, setInterestRate] = useState<number>(7.5);
  const [result, setResult] = useState<CalculatorResult | null>(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadFormData, setLeadFormData] = useState({
    name: '',
    phone: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;
    calculateLoan();
  }, [amount, term, interestRate, isHydrated]);

  const calculateLoan = () => {
    const monthlyRate = interestRate / 100 / 12;
    const monthlyPayment = (amount * monthlyRate * Math.pow(1 + monthlyRate, term)) / (Math.pow(1 + monthlyRate, term) - 1);
    const totalPayment = monthlyPayment * term;
    const totalInterest = totalPayment - amount;
    const potentialSavings = totalInterest * 0.15;

    setResult({
      monthlyPayment,
      totalPayment,
      totalInterest,
      potentialSavings,
    });
  };

  const handleLeadFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLeadFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLeadFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    await new Promise(resolve => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setSubmitSuccess(true);

    setTimeout(() => {
      setSubmitSuccess(false);
      setShowLeadForm(false);
      setLeadFormData({ name: '', phone: '', email: '' });
    }, 3000);
  };

  const isLeadFormValid = leadFormData.name && leadFormData.phone && leadFormData.email;

  if (!isHydrated) {
    return (
      <section id="calculator" className="py-16 lg:py-24 bg-muted/30">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
              Kalkulator kredytowy
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Oblicz swoją ratę w 30 sekund
            </p>
          </div>
          <div className="bg-card rounded-2xl h-96 animate-pulse" />
        </div>
      </section>
    );
  }

  return (
    <section id="calculator" className="py-16 lg:py-24 bg-muted/30">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full mb-6">
            <Icon name="CalculatorIcon" size={20} variant="solid" className="text-accent" />
            <span className="text-sm font-body font-medium text-accent">
              Kalkulator
            </span>
          </div>

          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
            Kalkulator kredytowy
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Oblicz swoją ratę w 30 sekund i zobacz, ile możesz zaoszczędzić z naszym doradztwem
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-card rounded-2xl shadow-card p-8">
            <h3 className="text-xl font-headline font-bold text-foreground mb-6">
              Parametry kredytu
            </h3>

            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-body font-medium text-foreground">
                    Kwota kredytu
                  </label>
                  <span className="text-lg font-headline font-bold text-primary">
                    {amount.toLocaleString('pl-PL')} PLN
                  </span>
                </div>
                <input
                  type="range"
                  min="50000"
                  max="5000000"
                  step="10000"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>50 000 PLN</span>
                  <span>5 000 000 PLN</span>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-body font-medium text-foreground">
                    Okres kredytowania
                  </label>
                  <span className="text-lg font-headline font-bold text-primary">
                    {term} miesięcy ({Math.floor(term / 12)} lat)
                  </span>
                </div>
                <input
                  type="range"
                  min="12"
                  max="360"
                  step="12"
                  value={term}
                  onChange={(e) => setTerm(Number(e.target.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>1 rok</span>
                  <span>30 lat</span>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-body font-medium text-foreground">
                    Oprocentowanie
                  </label>
                  <span className="text-lg font-headline font-bold text-primary">
                    {interestRate.toFixed(2)}%
                  </span>
                </div>
                <input
                  type="range"
                  min="3"
                  max="15"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>3%</span>
                  <span>15%</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-2xl shadow-card p-8">
            <h3 className="text-xl font-headline font-bold text-foreground mb-6">
              Wyniki obliczeń
            </h3>

            {result && (
              <div className="space-y-6">
                <div className="bg-primary/5 rounded-xl p-6">
                  <p className="text-sm text-muted-foreground mb-2">Miesięczna rata</p>
                  <p className="text-3xl font-headline font-bold text-primary">
                    {result.monthlyPayment.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} PLN
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-1">Całkowita kwota do spłaty</p>
                    <p className="text-lg font-headline font-bold text-foreground">
                      {result.totalPayment.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} PLN
                    </p>
                  </div>

                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-1">Całkowity koszt odsetek</p>
                    <p className="text-lg font-headline font-bold text-foreground">
                      {result.totalInterest.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} PLN
                    </p>
                  </div>
                </div>

                <div className="bg-success/10 rounded-xl p-6 border-2 border-success/20">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-success/20 rounded-lg">
                      <Icon name="SparklesIcon" size={20} variant="solid" className="text-success" />
                    </div>
                    <p className="text-sm font-body font-semibold text-foreground">
                      Potencjalne oszczędności z naszym doradztwem
                    </p>
                  </div>
                  <p className="text-2xl font-headline font-bold text-success">
                    {result.potentialSavings.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} PLN
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Dzięki negocjacjom lepszych warunków możesz zaoszczędzić nawet 15% kosztów odsetek
                  </p>
                </div>

                {!showLeadForm ? (
                  <button
                    onClick={() => setShowLeadForm(true)}
                    className="w-full px-6 py-4 bg-accent text-accent-foreground font-body font-semibold rounded-lg shadow-cta transition-all duration-250 hover:bg-accent/90 hover:shadow-lg flex items-center justify-center gap-2"
                  >
                    <span>Chcę zaoszczędzić - umów konsultację</span>
                    <Icon name="ArrowRightIcon" size={20} variant="outline" />
                  </button>
                ) : (
                  <div className="bg-muted/30 rounded-xl p-6">
                    {submitSuccess ? (
                      <div className="text-center py-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-success/10 rounded-full mx-auto mb-3">
                          <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-success" />
                        </div>
                        <p className="text-sm font-body font-semibold text-foreground mb-1">
                          Dziękujemy!
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Skontaktujemy się z Tobą w ciągu 24 godzin
                        </p>
                      </div>
                    ) : (
                      <form onSubmit={handleLeadFormSubmit} className="space-y-4">
                        <div>
                          <input
                            type="text"
                            name="name"
                            value={leadFormData.name}
                            onChange={handleLeadFormChange}
                            required
                            className="w-full px-4 py-2.5 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-250"
                            placeholder="Imię i nazwisko"
                          />
                        </div>
                        <div>
                          <input
                            type="tel"
                            name="phone"
                            value={leadFormData.phone}
                            onChange={handleLeadFormChange}
                            required
                            className="w-full px-4 py-2.5 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-250"
                            placeholder="Numer telefonu"
                          />
                        </div>
                        <div>
                          <input
                            type="email"
                            name="email"
                            value={leadFormData.email}
                            onChange={handleLeadFormChange}
                            required
                            className="w-full px-4 py-2.5 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-250"
                            placeholder="Adres e-mail"
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={!isLeadFormValid || isSubmitting}
                          className="w-full px-6 py-3 bg-accent text-accent-foreground font-body font-semibold rounded-lg shadow-cta transition-all duration-250 hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {isSubmitting ? (
                            <>
                              <div className="w-4 h-4 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
                              <span>Wysyłanie...</span>
                            </>
                          ) : (
                            'Wyślij'
                          )}
                        </button>
                      </form>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LoanCalculatorSection;